import type { ReactNode } from "react";
import { forwardRef } from "react";
import type { SwitchFieldProps as ReactAriaSwitchFieldProps } from "react-aria-components";
import {
  SwitchButton as ReactAriaSwitchButton,
  SwitchField as ReactAriaSwitchField,
} from "react-aria-components";

import { cn } from "../utils";
import { NcIconCross, NcIconTick } from "./nc-icons";

export interface NcSwitchProps extends ReactAriaSwitchFieldProps {
  children: ReactNode;
  hideLabel?: boolean;
}

export const NcSwitch = forwardRef<HTMLLabelElement, NcSwitchProps>(
  ({ children, hideLabel, className, ...props }, ref) => {
    return (
      <ReactAriaSwitchField data-nc="NcSwitch" className="contents" {...props}>
        <ReactAriaSwitchButton
          ref={ref}
          className={cn(
            "group flex max-w-max items-center gap-2 rounded",
            "data-disabled:text-disabled data-disabled:cursor-not-allowed",
            "data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
            className
          )}
        >
          <div
            className={cn(
              "print-color-adjust-exact flex aspect-2/1 h-7 shrink-0 cursor-pointer appearance-none items-center overflow-hidden",
              "text-ui-light border-ui bg-main rounded-full border p-0 transition-colors",
              "group-data-selected:bg-success group-data-disabled:bg-disabled group-data-selected:group-data-disabled:bg-disabled"
            )}
          >
            <div
              className={cn(
                "flex origin-center -translate-x-7 items-center gap-1 px-2 transition-transform duration-300 ease-in-out",
                "group-data-pressed:-translate-x-3 group-data-pressed:group-data-pressed:-translate-x-3 group-data-selected:translate-x-0"
              )}
            >
              <NcIconTick className="h-4 shrink-0 transition-opacity group-data-pressed:opacity-0" />
              <div className="bg-ui-light m-0.5 h-5.5 w-5.5 shrink-0 rounded-full"></div>
              <NcIconCross className="h-4 shrink-0 transition-opacity group-data-pressed:opacity-0" />
            </div>
          </div>
          <div className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 font-bold">
            {hideLabel ? <span className="sr-only">{children}</span> : children}
          </div>
        </ReactAriaSwitchButton>
      </ReactAriaSwitchField>
    );
  }
);
